import React, { useState, useEffect, useRef } from "react";
import { Button } from "../ui";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useTranslation } from "react-i18next";
import { api } from "../../services";
import NativeInput from "../NativeInput";
import { getPhoneLikeNumberError } from "../../utils/validators";

const CategoryModal = ({
  modalShow = false,
  category,
  loading,
  onClose,
  onUpdateSuccess,
}) => {
  const { t, i18n } = useTranslation("translation");
  const isArabic = i18n.language === "ar";
  const cancelButtonRef = useRef(null);
  const isInitialMount = useRef(true);

  const [nameEn, setNameEn] = useState(category?.name?.en || "");
  const [nameAr, setNameAr] = useState(category?.name?.ar || "");
  const [isLoading, setIsLoading] = useState(loading);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!isInitialMount.current) {
      validateField("nameEn", nameEn);
      validateField("nameAr", nameAr);
    } else {
      isInitialMount.current = false;
    }
  }, [nameEn, nameAr]);

  const hasErrors = () => Object.values(errors).some((error) => error !== "");

  useEffect(() => {
    if (modalShow) {
      if (category) {
        setNameEn(category.name?.en || "");
        setNameAr(category.name?.ar || "");
      } else {
        setNameEn("");
        setNameAr("");
      }
      setErrors({});
      isInitialMount.current = true;
    }
  }, [modalShow, category]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && !isLoading) {
        handleClose();
      }
    };

    if (modalShow) {
      document.addEventListener("keydown", handleKeyDown);
      if (cancelButtonRef.current) {
        cancelButtonRef.current.focus();
      }
      return () => document.removeEventListener("keydown", handleKeyDown);
    }
  }, [modalShow, isLoading]);

  const validateField = (field, value) => {
    if (field === "nameEn") {
      if (!value.trim()) {
        setErrors((prev) => ({
          ...prev,
          nameEn: t("English name is required."),
        }));
      } else {
        const phoneError = getPhoneLikeNumberError(value, t);
        if (phoneError) {
          setErrors((prev) => ({ ...prev, nameEn: phoneError }));
        } else if (!/^[a-zA-Z0-9\s]*$/.test(value)) {
          setErrors((prev) => ({
            ...prev,
            nameEn: t(
              "English name must only contain English letters and numbers."
            ),
          }));
        } else {
          setErrors((prev) => ({ ...prev, nameEn: "" }));
        }
      }
    } else if (field === "nameAr") {
      if (!value.trim()) {
        setErrors((prev) => ({
          ...prev,
          nameAr: t("Arabic name is required."),
        }));
      } else {
        const phoneError = getPhoneLikeNumberError(value, t);
        if (phoneError) {
          setErrors((prev) => ({ ...prev, nameAr: phoneError }));
        } else if (!/^[\u0600-\u06FF0-9\s]*$/.test(value)) {
          setErrors((prev) => ({
            ...prev,
            nameAr: t(
              "Arabic name must only contain Arabic letters and numbers."
            ),
          }));
        } else {
          setErrors((prev) => ({ ...prev, nameAr: "" }));
        }
      }
    }
  };

  const handleBlur = (field, value) => {
    validateField(field, value);
  };

  const handleClose = () => {
    if (onClose) onClose();
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && !isLoading) {
      handleClose();
    }
  };

  const onSubmit = async () => {
    const newErrors = {};

    if (!nameEn.trim()) {
      newErrors.nameEn = t("English name is required.");
    } else {
      const phoneErrorEn = getPhoneLikeNumberError(nameEn, t);
      if (phoneErrorEn) newErrors.nameEn = phoneErrorEn;
      else if (!/^[a-zA-Z0-9\s]*$/.test(nameEn)) {
        newErrors.nameEn = t(
          "English name must only contain English letters and numbers."
        );
      }
    }

    if (!nameAr.trim()) {
      newErrors.nameAr = t("Arabic name is required.");
    } else {
      const phoneErrorAr = getPhoneLikeNumberError(nameAr, t);
      if (phoneErrorAr) newErrors.nameAr = phoneErrorAr;
      else if (!/^[\u0600-\u06FF0-9\s]*$/.test(nameAr)) {
        newErrors.nameAr = t(
          "Arabic name must only contain Arabic letters and numbers."
        );
      }
    }

    setErrors(newErrors);

    if (Object.values(newErrors).some((error) => error !== "")) {
      return;
    }

    setIsLoading(true);

    try {
      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("Authentication token not found");
      }

      const response = await api.put(
        `/categories/${category._id}`,
        { nameEn, nameAr },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data) {
        toast.success(t("Category updated successfully."));
        if (onUpdateSuccess) onUpdateSuccess(response.data);
        handleClose();
      }
    } catch (error) {
      console.error("Update category error:", error);
      const errorMessage =
        error.response?.data?.message || t("Category Update Failed.");
      toast.error(t(errorMessage, { defaultValue: errorMessage }));
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !hasErrors()) {
      onSubmit();
    }
  };

  if (!modalShow) return null;

  const submitButton = (
    <Button
      type="button"
      color="primary"
      size="md"
      onClick={onSubmit}
      disabled={isLoading || hasErrors()}
      className={isArabic ? "me-2" : ""}
    >
      {isLoading ? (
        <>
          <span
            className="spinner-border spinner-border-sm me-2"
            role="status"
            aria-hidden="true"
          />
          {t("updating")}
        </>
      ) : (
        t("update")
      )}
    </Button>
  );

  const cancelButton = (
    <Button
      ref={cancelButtonRef}
      type="button"
      color="secondary"
      size="md"
      onClick={handleClose}
      disabled={isLoading}
      className={isArabic ? "" : "me-2"}
    >
      {t("cancel")}
    </Button>
  );

  return (
    <div
      className="modal fade show d-block oh-modal"
      style={{
        backgroundColor: "rgba(0,0,0,0.5)",
        backdropFilter: "blur(4px)",
        WebkitBackdropFilter: "blur(4px)",
      }}
      onClick={handleBackdropClick}
    >
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">{t("updatecategory")}</h5>
            <button
              type="button"
              className="btn-close"
              onClick={handleClose}
              disabled={isLoading}
              aria-label="Close"
            />
          </div>

          <div className="modal-body" onKeyPress={handleKeyPress}>
            <NativeInput
              wrapperClass="mb-3"
              label={t("store")}
              value={
                category?.storeName ||
                category?.store ||
                category?.storeId ||
                "-"
              }
              readOnly
              onFocus={(e) => e.target.blur()}
              style={{
                backgroundColor: "#f5f5f5",
                opacity: 1,
                cursor: "not-allowed",
              }}
            />
            <NativeInput
              wrapperClass="mb-3"
              label={
                <span>
                  {t("nameen")} <span className="text-danger">*</span>
                </span>
              }
              value={nameEn}
              onBlur={(e) => handleBlur("nameEn", e.target.value)}
              onChange={(e) => {
                const value = e.target.value;
                setNameEn(value);
                validateField("nameEn", value);
              }}
              required
              invalid={!!errors.nameEn}
              feedback={errors.nameEn}
            />
            <NativeInput
              wrapperClass="mb-0"
              label={
                <span>
                  {t("namear")} <span className="text-danger">*</span>
                </span>
              }
              value={nameAr}
              onBlur={(e) => handleBlur("nameAr", e.target.value)}
              onChange={(e) => {
                const value = e.target.value;
                setNameAr(value);
                validateField("nameAr", value);
              }}
              required
              invalid={!!errors.nameAr}
              feedback={errors.nameAr}
            />
          </div>

          <div className="modal-footer justify-content-end">
            {isArabic ? (
              <>
                {submitButton}
                {cancelButton}
              </>
            ) : (
              <>
                {cancelButton}
                {submitButton}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CategoryModal;
